import type { ReactNode } from "react";
import type { PatientChatRequestKind } from "../../../lib/patientChat";
import { PromptResultTools } from "../../noteList/SmartAssistPromptTools";
import type { SmartAssistCitations } from "../core/types";

interface SmartAssistReplyProps {
  reply: string;
  isAwaitingResponse: boolean;
  requestKind: PatientChatRequestKind | null;
  citations: SmartAssistCitations;
  promptReformatSummary?: string;
  onMacroSaved?: () => unknown;
  /** Rendered in the card header and again below the reply once it settles. */
  actions: ReactNode;
}

export default function SmartAssistReply({
  reply,
  isAwaitingResponse,
  requestKind,
  citations,
  promptReformatSummary,
  onMacroSaved,
  actions,
}: SmartAssistReplyProps) {
  const externalSources = citations?.externalSources || [];

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-sky-100 bg-white px-4 py-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs font-semibold text-light-grey">
          {isAwaitingResponse ? "Generating response…" : "Draft response"}
        </div>
        {!isAwaitingResponse && actions}
      </div>
      <div className="break-words whitespace-pre-wrap text-sm leading-6 text-accent1 [overflow-wrap:anywhere]">
        {reply}
      </div>
      {requestKind === "clinical_intelligence" && (
        <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
          Current response is based on the note and chart history loaded into Smart Assist only.
        </div>
      )}
      {externalSources.length > 0 && (
        <div className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-light-grey">
            External citations
          </div>
          <div className="space-y-3">
            {externalSources.map((source, index) => (
              <div
                key={`${source.url}-${index}`}
                className="rounded-md border border-gray-200 bg-white px-3 py-3 text-sm text-accent1"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate font-semibold text-accent1 underline"
                      title={source.url}
                    >
                      {source.title || source.url}
                    </a>
                    <div className="mt-1 text-xs text-light-grey">{source.sourceType}</div>
                    <div className="mt-1 break-all text-xs text-light-grey" title={source.url}>
                      {source.url}
                    </div>
                  </div>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 rounded-md border border-accent1 px-2 py-1 text-xs font-semibold text-accent1 hover:bg-secondary-grey"
                  >
                    Open source
                  </a>
                </div>
                {source.citedText && (
                  <div className="mt-3 whitespace-pre-wrap rounded bg-secondary-grey px-2 py-2 text-xs">
                    {source.citedText}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {requestKind === "prompt_reformat" && !isAwaitingResponse && (
        <PromptResultTools
          promptText={reply}
          summary={promptReformatSummary}
          onMacroSaved={onMacroSaved}
        />
      )}
      {!isAwaitingResponse && <div className="mt-4 border-t border-gray-200 pt-3">{actions}</div>}
    </div>
  );
}
